import AdminShell from "@/components/admin/AdminShell";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Camera, LayoutGrid, ArrowRight, Sparkles, Calculator } from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const name = user?.name?.split(" ")[0] || "there";

  return (
    <AdminShell>
      <div className="mx-auto max-w-5xl px-6 md:px-8 py-14">
        <div className="rounded-3xl bg-white sb-hairline sb-shadow-lg p-10 md:p-14 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#299fe0]/10 text-[#299fe0] text-xs uppercase tracking-[0.25em] font-semibold">
            <Sparkles size={13} />
            Sky Basket Studio
          </div>
          <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl text-slate-900 tracking-tight">
            Hi {name} — what would you like to do today?
          </h1>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">
            Pick a workflow below. Everything runs in your browser, so nothing
            gets uploaded to a server.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/admin/passport"
              data-testid="dashboard-passport-card"
              className="group relative overflow-hidden rounded-3xl p-8 text-left transition-all hover:-translate-y-1 hover:shadow-2xl sb-shadow"
              style={{
                background:
                  "linear-gradient(150deg, #299fe0 0%, #1a86c4 100%)",
              }}
            >
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center text-white">
                  <Camera size={26} strokeWidth={2} />
                </div>
                <div className="mt-8 text-white/80 text-xs uppercase tracking-[0.28em] font-semibold">
                  Workflow 01
                </div>
                <h2 className="mt-2 font-display font-black text-white text-2xl md:text-3xl leading-tight">
                  I want to create a passport size photo
                </h2>
                <p className="mt-3 text-white/85 leading-relaxed">
                  Upload a photo, crop it manually, and lay out multiple copies
                  on a print-ready A4 sheet with cutting guides.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-white font-semibold">
                  Start passport workflow{" "}
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </div>
              </div>
            </Link>

            <Link
              to="/admin/collage"
              data-testid="dashboard-collage-card"
              className="group relative overflow-hidden rounded-3xl p-8 text-left transition-all hover:-translate-y-1 hover:shadow-2xl sb-shadow"
              style={{
                background:
                  "linear-gradient(150deg, #cb05a5 0%, #7a0d70 100%)",
              }}
            >
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center text-white">
                  <LayoutGrid size={26} strokeWidth={2} />
                </div>
                <div className="mt-8 text-white/80 text-xs uppercase tracking-[0.28em] font-semibold">
                  Workflow 02
                </div>
                <h2 className="mt-2 font-display font-black text-white text-2xl md:text-3xl leading-tight">
                  I want to create a collage of photos
                </h2>
                <p className="mt-3 text-white/85 leading-relaxed">
                  Choose a layout, upload photos, arrange them, and download
                  each page — or all pages combined — as a print-ready PDF.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-white font-semibold">
                  Start collage workflow{" "}
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </div>
              </div>
            </Link>
          </div>

          <Link
            to="/admin/pos"
            data-testid="dashboard-pos-card"
            className="group mt-6 block relative overflow-hidden rounded-3xl p-8 text-left transition-all hover:-translate-y-1 hover:shadow-2xl sb-shadow bg-slate-900"
          >
            <div className="absolute -bottom-16 -left-10 w-56 h-56 rounded-full bg-[#299fe0]/20 blur-3xl" />
            <div className="relative flex flex-col md:flex-row md:items-center gap-6">
              <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center text-white shrink-0">
                <Calculator size={26} strokeWidth={2} />
              </div>
              <div className="flex-1">
                <div className="text-white/60 text-xs uppercase tracking-[0.28em] font-semibold">
                  Workflow 03 · Stationery Pro
                </div>
                <h2 className="mt-2 font-display font-black text-white text-2xl md:text-3xl leading-tight">
                  Billing counter, change calculator & sales
                </h2>
                <p className="mt-3 text-white/80 leading-relaxed">
                  Add item prices on a big keypad, see the exact notes and coins to
                  return, save the sale, and print on a Bluetooth thermal printer.
                </p>
              </div>
              <div className="inline-flex items-center gap-2 text-white font-semibold">
                Open counter
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </div>
            </div>
          </Link>
        </div>
      </div>
    </AdminShell>
  );
}
